import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { activityService } from '../../services/activityService';
import { useAuth } from '../../contexts/AuthContext';

const ACTIVITY_CONFIG = {
  task_completed:    { icon: 'CheckCircle2',  color: 'text-emerald-600', bg: 'bg-emerald-100',  label: 'Task Completed' },
  snag_created:      { icon: 'AlertOctagon',  color: 'text-amber-600',   bg: 'bg-amber-100',    label: 'Issue Created' },
  snag_resolved:     { icon: 'CheckSquare',   color: 'text-blue-600',    bg: 'bg-blue-100',     label: 'Issue Closed' },
  snag_updated:      { icon: 'Edit3',         color: 'text-slate-600',   bg: 'bg-slate-100',    label: 'Issue Updated' },
  incident_created:  { icon: 'ShieldAlert',   color: 'text-red-600',     bg: 'bg-red-100',      label: 'Incident Reported' },
  document_uploaded: { icon: 'FileUp',        color: 'text-purple-600',  bg: 'bg-purple-100',   label: 'Document Uploaded' },
  document_expiring: { icon: 'Clock',         color: 'text-orange-600',  bg: 'bg-orange-100',   label: 'Document Expiring' },
  site_created:      { icon: 'Building2',     color: 'text-accent',      bg: 'bg-accent/10',    label: 'Site Created' },
  worker_assigned:   { icon: 'UserPlus',      color: 'text-teal-600',    bg: 'bg-teal-100',     label: 'Worker Assigned' },
  user_invited:      { icon: 'Mail',          color: 'text-indigo-600',  bg: 'bg-indigo-100',   label: 'User Invited' },
  user_joined:       { icon: 'UserCheck',     color: 'text-green-600',   bg: 'bg-green-100',    label: 'User Joined' },
  report_exported:   { icon: 'FileBarChart',  color: 'text-cyan-600',    bg: 'bg-cyan-100',     label: 'Report Exported' },
  default:           { icon: 'Activity',      color: 'text-muted-foreground', bg: 'bg-muted',   label: 'Activity' },
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Activity' },
  { value: 'user_invited', label: 'User Invited' },
  { value: 'user_joined', label: 'User Joined' },
  { value: 'site_created', label: 'Site Created' },
  { value: 'task_completed', label: 'Task Completed' },
  { value: 'snag_created', label: 'Issue Created' },
  { value: 'snag_resolved', label: 'Issue Closed' },
  { value: 'document_uploaded', label: 'Document Uploaded' },
  { value: 'report_exported', label: 'Report Exported' },
];

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const AdminActivityLog = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [newIds, setNewIds] = useState(new Set());
  const unsubscribeRef = useRef(null);

  const isAdmin = userProfile?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin-dashboard');
      return;
    }
    loadActivities();

    // Real-time subscription
    if (unsubscribeRef?.current) unsubscribeRef?.current();
    const unsubscribe = activityService?.subscribeToAllActivities((newActivity) => {
      setActivities(prev => {
        if (prev?.some(a => a?.id === newActivity?.id)) return prev;
        return [newActivity, ...prev]?.slice(0, 100);
      });
      setNewIds(prev => new Set([...prev, newActivity?.id]));
      setTimeout(() => {
        setNewIds(prev => {
          const next = new Set(prev);
          next?.delete(newActivity?.id);
          return next;
        });
      }, 5000);
    });
    unsubscribeRef.current = unsubscribe;

    return () => {
      if (unsubscribeRef?.current) {
        unsubscribeRef?.current();
        unsubscribeRef.current = null;
      }
    };
  }, [isAdmin]);

  const loadActivities = async () => {
    setLoading(true);
    try {
      const { data, error: err } = await activityService?.getAllActivities({ limit: 100 });
      if (err) {
        console.error('[AdminActivityLog] load activities failed:', { code: err?.code, message: err?.message });
      }
      setActivities(data || []);
    } catch (err) {
      console.error('[AdminActivityLog] load activities threw:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleActivityClick = (activity) => {
    const type = activity?.activity_type || activity?.type;
    switch (type) {
      case 'document_uploaded':
      case 'document_expiring': navigate('/document-management');
        break;
      case 'incident_created':
        if (activity?.site_id) navigate(`/sites/${activity?.site_id}?tab=incidents`);
        else navigate('/incident-reporting');
        break;
      case 'snag_created': case'snag_updated': case'snag_resolved':
        if (activity?.site_id) navigate(`/sites/${activity?.site_id}?tab=snags`);
        else navigate('/snags');
        break;
      case 'task_completed':
        if (activity?.site_id) navigate(`/sites/${activity?.site_id}?tab=tasks`);
        break;
      case 'site_created':
        if (activity?.site_id) navigate(`/sites/${activity?.site_id}`);
        else navigate('/sites');
        break;
      case 'user_invited': case'user_joined': navigate('/invite-users');
        break;
      case 'report_exported':
        navigate('/report-builder');
        break;
      default:
        break;
    }
  };

  const filteredActivities = activities?.filter(a => {
    if (filterType === 'all') return true;
    return (a?.activity_type || a?.type) === filterType;
  });

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Activity Log - SiteProof</title>
      </Helmet>
      <Header />
      <div className="pt-[72px]">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate('/admin-dashboard')}
              className="flex items-center justify-center w-9 h-9 rounded-md border border-border hover:bg-muted transition-colors"
            >
              <Icon name="ArrowLeft" size={18} />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-foreground">Activity Log</h1>
              <p className="text-sm text-muted-foreground">Recent company-wide activity</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Live
              </span>
              <Button variant="outline" size="sm" iconName="RefreshCw" onClick={loadActivities} disabled={loading}>
                Refresh
              </Button>
            </div>
          </div>

          {/* Filter */}
          <div className="bg-card rounded-lg border border-border p-4 mb-6">
            <div className="flex flex-wrap gap-2">
              {FILTER_OPTIONS?.map(opt => (
                <button
                  key={opt?.value}
                  onClick={() => setFilterType(opt?.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    filterType === opt?.value
                      ? 'bg-accent text-white border-accent' :'bg-background border-border text-muted-foreground hover:border-accent/50 hover:text-foreground'
                  }`}
                >
                  {opt?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Activity list */}
          <div className="bg-card rounded-lg shadow-elevation-1 border border-border">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
              </div>
            ) : filteredActivities?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-4">
                <Icon name="Activity" size={48} className="text-muted-foreground mb-3" />
                <p className="text-muted-foreground text-center">No activity found</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredActivities?.map((activity) => {
                  const type = activity?.activity_type || activity?.type;
                  const cfg = ACTIVITY_CONFIG?.[type] || ACTIVITY_CONFIG?.default;
                  const isNew = newIds?.has(activity?.id);
                  const userName = activity?.user?.full_name || null;
                  const siteName = activity?.site?.site_name || null;
                  const isClickable = true;

                  return (
                    <div
                      key={activity?.id}
                      onClick={() => handleActivityClick(activity)}
                      className={`flex gap-4 p-4 cursor-pointer hover:bg-muted/40 transition-colors ${isNew ? 'bg-accent/5' : ''}`}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => (e?.key === 'Enter' || e?.key === ' ') && handleActivityClick(activity)}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        <div className={`flex items-center justify-center w-9 h-9 rounded-full ${cfg?.bg}`}>
                          <Icon name={cfg?.icon} size={16} className={cfg?.color} />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground leading-snug">{activity?.title}</p>
                            {activity?.description && (
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{activity?.description}</p>
                            )}
                            <div className="flex flex-wrap items-center gap-2 mt-1.5">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg?.bg} ${cfg?.color}`}>
                                {cfg?.label}
                              </span>
                              {userName && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Icon name="User" size={11} />
                                  {userName}
                                </span>
                              )}
                              {siteName && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Icon name="Building2" size={11} />
                                  {siteName}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {isNew && <span className="w-2 h-2 rounded-full bg-accent" />}
                            <p className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatRelativeTime(activity?.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {filteredActivities?.length > 0 && (
            <p className="text-xs text-muted-foreground text-center mt-4">
              Showing {filteredActivities?.length} activities
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminActivityLog;
